import { useState, useEffect } from 'react';
import { PageHeader } from '@/components/shared/PageHeader';
import { SectionReveal } from '@/components/shared/SectionReveal';
import { TestimonialsCarousel } from '@/components/TestimonialsCarousel';
import { getClients } from '@/lib/api';
import { Skeleton } from '@/components/ui/skeleton';
import { SEO } from '@/components/SEO';

const collaborationTypes = [
  { type: 'Studios & Platforms', desc: 'Feature films, series, and original content for major streaming and broadcast platforms.' },
  { type: 'Agencies & Brands', desc: 'Commercial campaigns, brand films, and content production for leading agencies and global brands.' },
  { type: 'Independent Filmmakers', desc: 'Production services, post-production, and equipment support for independent projects.' },
  { type: 'International Productions', desc: 'Full-service Mexico production for international crews, agencies, and studios.' },
];

export default function ClientsPage() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getClients()
      .then(setClients)
      .finally(() => setLoading(false));
  }, []); // Only fetch once on mount

  // Group by category
  const grouped = clients.reduce((acc, c) => {
    const cat = c.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(c);
    return acc;
  }, {});

  const categoryLabels = {
    studio: 'Studios & Platforms',
    agency: 'Agencies',
    brand: 'Brands',
    filmmaker: 'Filmmakers',
  };

  return (
    <div>
      <SEO 
        title="Our Clients — HBO, Discovery, NatGeo & More | Baklight Films"
        description="Trusted by international studios, agencies, and brands including HBO, Discovery Channel, National Geographic, Nestlé, and Sony Pictures."
        path="/clients"
      />
      <PageHeader
        meta="Clients & Trust"
        title="Trusted by the best."
        subtitle="International brands, agencies, studios, and filmmakers trust Baklight to deliver cinematic production at the highest level."
      />

      {/* Client Wall */}
      <section className="pb-14 sm:pb-20">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {[...Array(10)].map((_, i) => (
                <Skeleton key={i} className="h-20 rounded-lg" />
              ))}
            </div>
          ) : (
            <div className="space-y-12">
              {Object.entries(grouped).map(([cat, items]) => (
                <SectionReveal key={cat}>
                  <p className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-5">{categoryLabels[cat] || cat}</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                    {items.map((client) => (
                      <div
                        key={client.id || client.name}
                        className="flex items-center justify-center py-6 px-4 rounded-lg border border-[hsl(var(--bk-line))] bg-[hsl(var(--bk-bg-elev))] hover:border-[hsl(var(--bk-line-2))] transition-colors"
                      >
                        <span className="font-body text-sm text-[hsl(var(--bk-fg-muted))] text-center">{client.name}</span>
                      </div>
                    ))}
                  </div>
                </SectionReveal>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Collaboration Types */}
      <section className="py-14 sm:py-20 bg-[hsl(var(--bk-bg-elev))]">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
          <SectionReveal>
            <p className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-3">How We Collaborate</p>
            <h2 className="font-display text-2xl md:text-3xl tracking-[-0.02em] text-[hsl(var(--bk-fg))] mb-12">
              Partnerships across the industry.
            </h2>
          </SectionReveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {collaborationTypes.map((item, i) => (
              <SectionReveal key={item.type} delay={i * 0.1}>
                <div className="p-6 rounded-lg border border-[hsl(var(--bk-line))] bg-[hsl(var(--bk-bg))]">
                  <h3 className="font-display text-lg text-[hsl(var(--bk-fg))] mb-2">{item.type}</h3>
                  <p className="font-body text-sm text-[hsl(var(--bk-fg-muted))] leading-relaxed">{item.desc}</p>
                </div>
              </SectionReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Carousel */}
      <TestimonialsCarousel />

      {/* Trust Signals */}
      <section className="py-14 sm:py-20">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
          <SectionReveal>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
              {[
                { num: '20+', label: 'Years of Production Experience' },
                { num: '12+', label: 'Telly Awards Won' },
                { num: '320+', label: 'Projects Delivered' },
              ].map((stat) => (
                <div key={stat.label}>
                  <p className="font-display text-4xl md:text-5xl text-[hsl(var(--bk-accent))]">{stat.num}</p>
                  <p className="font-body text-sm text-[hsl(var(--bk-fg-muted))] mt-2">{stat.label}</p>
                </div>
              ))}
            </div>
          </SectionReveal>
        </div>
      </section>
    </div>
  );
}
